import IconClose from 'components/icons/close';
import React, {
  FunctionComponent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import ReactModal from 'react-modal';
import { BigCommerceProductVariantCleaned } from '../../lib/bigcommerce/types';
import './tila-modal.css';
import TilaReceipt from './tila-receipt';

interface simpleMap {
  [name: string]: string;
}

export type TilaModalProps = ReactModal.Props & {
  onConfirm?: () => void;
  onSubmit?: () => void;
  sku: string;
};

export type TilaMetafieldsObject = simpleMap & {
  'payment-type': string;
  'num-remaining-payments': string;
  'additional-payments-amount': string;
  'annual-percent-rate': string;
  'finance-charge': string;
  'amount-financed': string;
  'total-of-payments': string;
  'total-sales-price': string;
};

const noop = () => {};

export type TilaProduct = Omit<BigCommerceProductVariantCleaned, 'metafields'> & {
  metafields: TilaMetafieldsObject;
  name: string;
};

const TilaModal: FunctionComponent<TilaModalProps> = ({
  onConfirm: _onConfirm = noop,
  onSubmit = noop,
  onRequestClose = noop,
  isOpen,
  sku,
  onAfterClose,
  ...rest
}) => {
  const [checkboxes, setCheckboxes] = useState({
    checkbox1: false,
    checkbox2: false,
    checkbox3: false
  });

  const [validationErrors, setValidationErrors] = useState({
    checkbox1: false,
    checkbox2: false,
    checkbox3: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    if (allChecked) {
      setValidationErrors({
        checkbox1: false,
        checkbox2: false,
        checkbox3: false
      });
      setIsSubmitting(true);
      // Call onSubmit which will trigger the purchase flow
      onSubmit();
      // Note: Modal stays open, submission state handled by parent
    } else {
      // Show validation errors for unchecked checkboxes
      setValidationErrors({
        checkbox1: !checkboxes.checkbox1,
        checkbox2: !checkboxes.checkbox2,
        checkbox3: !checkboxes.checkbox3
      });
      event.preventDefault();
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, checkboxName: string) => {
    setCheckboxes((prevState) => ({
      ...prevState,
      [checkboxName]: e.target.checked
    }));

    // Clear validation error when checkbox is checked
    if (e.target.checked) {
      setValidationErrors((prevState) => ({
        ...prevState,
        [checkboxName]: false
      }));
    }
  };

  const handleClose = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      onRequestClose(event);
      document.body.focus();
    },
    [onRequestClose]
  );

  const [product, setProduct] = useState<TilaProduct | undefined>();
  const [productString, setProductString] = useState<string>('');

  useEffect(() => {
    const getProducts = async () => {
      const response = await fetch(`/api/variant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sku
        })
      });

      const result = await response.json();
      if (!result) return;

      const tilaProduct =
        mapTilaProduct(
          result.title,
          result.variants.find((variant: BigCommerceProductVariantCleaned) => variant.sku === sku)
        ) || undefined;
      const dispString = generateProductString(tilaProduct);

      setProduct(tilaProduct);
      setProductString(dispString);
    };

    try {
      getProducts();
    } catch (error) {
      console.error(error);
    }
  }, [sku]);

  const allChecked = checkboxes.checkbox1 && checkboxes.checkbox2 && checkboxes.checkbox3;

  const generateProductString = (product: TilaProduct | undefined): string => {
    if (!product) return '';
    const name = product.name;
    const numRemainingPayments = parseFloat(product.metafields['num-remaining-payments']) + 1;
    const productDetails = `${name} - ${numRemainingPayments}Pay`;

    return `I want to secure my peace of mind with a ${productDetails} and the convenience of low monthly payments with no credit check. I promise... I'm good for it!`;
  };

  // If user presses 'Esc' key, close the modal
  const escKeyPressed = useCallback(
    (event: any) => {
      if (event.key === 'Escape') {
        onRequestClose(event);
        document.body.focus();
      }
    },
    [onRequestClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', escKeyPressed, false);
    return () => document.removeEventListener('keydown', escKeyPressed, false);
  }, [escKeyPressed]);

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
    }
  }, [isOpen]);

  return (
    <ReactModal
      {...rest}
      isOpen={isOpen}
      onAfterClose={onAfterClose}
      ariaHideApp={process.env.NODE_ENV !== 'test'}
      bodyOpenClassName="has-activeModal"
      className={{
        base: 'tila modal optimizedCheckout-contentPrimary',
        afterOpen: 'modal--afterOpen',
        beforeClose: 'modal--beforeClose'
      }}
      closeTimeoutMS={200}
      onRequestClose={onRequestClose}
      overlayClassName={{
        base: 'tila modalOverlay',
        afterOpen: 'modalOverlay--afterOpen',
        beforeClose: 'modalOverlay--beforeClose'
      }}
      shouldCloseOnEsc={!isSubmitting}
      shouldCloseOnOverlayClick={!isSubmitting}
    >
      <header className="tila modal-header">
        <button
          className="modal-close"
          data-test="modal-close-button"
          onClick={isSubmitting ? () => {} : handleClose}
          disabled={isSubmitting}
          style={{ pointerEvents: isSubmitting ? 'none' : 'auto', opacity: isSubmitting ? 0.5 : 1 }}
        >
          <IconClose />
        </button>
      </header>

      <div className="modal-body" data-test="modal-body">
        <h1 className="modal-header-text">Easy Payment Plan Claim Form</h1>
        <div className="tila-modal-main">
          <div className="tila-modal-main-top">
            <div className="tila-modal-main-top-agreement">
              <div className="tila-modal-main-top-agreement-checkmark">
                {/* Intentionally empty div, image added here via CSS background-image */}
              </div>
              <div className="tila-modal-main-top-agreement-info">{productString}</div>
            </div>
            <div className="tila-modal-main-top-agreement-warning">
              Please read the information below and check all boxes to claim your Easy Payment Plan
              now.
            </div>
          </div>

          <div className="tila-modal-main-bottom">
            <div
              className={`tila-modal-main-bottom-part ${
                validationErrors.checkbox1 ? 'validation-error' : ''
              }`}
            >
              <div className="tila-modal-main-bottom-part-check">
                <h3 className="tila-modal-main-bottom-part-check-heading">
                  <input
                    type="checkbox"
                    id="checkbox1"
                    checked={checkboxes.checkbox1}
                    onChange={(e) => handleCheckboxChange(e, 'checkbox1')}
                  />
                  <label htmlFor="checkbox1">
                    Receipt of <abbr title="Truth in Lending Act">TILA</abbr> Disclosure
                  </label>
                </h3>
                {validationErrors.checkbox1 && (
                  <div className="validation-error-message">Please check this box to continue.</div>
                )}
              </div>
              <div className="tila-modal-main-bottom-part-info">
                <p>
                  I confirm receipt of the TILA disclosure and accept all purchase and financing
                  terms.
                </p>

                <TilaReceipt product={product} />
              </div>
            </div>

            <div
              className={`tila-modal-main-bottom-part ${
                validationErrors.checkbox2 ? 'validation-error' : ''
              }`}
            >
              <div className="tila-modal-main-bottom-part-check">
                <h3 className="tila-modal-main-bottom-part-check-heading">
                  <input
                    type="checkbox"
                    id="checkbox2"
                    checked={checkboxes.checkbox2}
                    onChange={(e) => handleCheckboxChange(e, 'checkbox2')}
                  />
                  <label htmlFor="checkbox2">Recurring Payment Authorization</label>
                </h3>
                {validationErrors.checkbox2 && (
                  <div className="validation-error-message">Please check this box to continue.</div>
                )}
              </div>
              <div className="tila-modal-main-bottom-part-info">
                <p>
                  By clicking this box, I authorize 4Patriots to automatically charge my card for
                  monthly payments until my balance is paid in full, or until I amend this
                  authorization.
                </p>
              </div>
            </div>

            <div
              className={`tila-modal-main-bottom-part ${
                validationErrors.checkbox3 ? 'validation-error' : ''
              }`}
            >
              <div className="tila-modal-main-bottom-part-check">
                <h3 className="tila-modal-main-bottom-part-check-heading">
                  <input
                    type="checkbox"
                    id="checkbox3"
                    checked={checkboxes.checkbox3}
                    onChange={(e) => handleCheckboxChange(e, 'checkbox3')}
                  />
                  <label htmlFor="checkbox3">Permission to Contact</label>
                </h3>
                {validationErrors.checkbox3 && (
                  <div className="validation-error-message">Please check this box to continue.</div>
                )}
              </div>
              <div className="tila-modal-main-bottom-part-info">
                <p>
                  I authorize 4Patriots and its agents to contact me at any number I provide, using
                  autodialers, prerecorded or artificial voice, and text for info, marketing, or
                  debt collection; giving a number is optional, and I confirm I own it.
                </p>
                <h4 className="tila-modal-main-bottom-part-info-heading">Contact Information:</h4>
                <p>
                  Opt out of marketing &amp; informational by replying{' '}
                  <span className="uppercase">Stop</span>. Debt collection will comply with FDCPA
                  &amp; applicable laws. Reach us at <a href="tel:1-800-798-3351">800-798-3351</a>{' '}
                  or <a href="mailto:paymentplans@4patriots.com">paymentplans@4patriots.com</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="tila-modal-footer">
        <button type="button" className="cta-button" onClick={handleConfirm}>
          <span className="cta-button-text">Click to Confirm</span>
          {isSubmitting && <div className="loadingOverlay" />}
        </button>

        <button className="tila-modal-footer-back-button" onClick={handleClose}>
          No thanks, take me back to the offer.
        </button>
      </footer>
    </ReactModal>
  );
};

export default TilaModal;

const mapTilaProduct = (
  name: string,
  variant: BigCommerceProductVariantCleaned
): TilaProduct | null => {
  let variantsWithTilaMetafields: TilaProduct | null = null;

  const metafieldsObject: TilaMetafieldsObject = {
    'payment-type': '',
    'num-remaining-payments': '',
    'additional-payments-amount': '',
    'annual-percent-rate': '',
    'finance-charge': '',
    'amount-financed': '',
    'total-of-payments': '',
    'total-sales-price': ''
  };

  variant.tilaMetafields.forEach((metafield: any) => {
    if (Object.hasOwn(metafieldsObject, metafield.key)) {
      metafieldsObject[metafield.key] = metafield.value;
    }
  });

  variant.paymentMetafields.forEach((metafield: any) => {
    if (Object.hasOwn(metafieldsObject, metafield.key)) {
      metafieldsObject[metafield.key] = metafield.value;
    }
  });

  const hasTilaMetafield = Object.values(metafieldsObject).some((value) => value !== '');
  const isPaymentPlan = metafieldsObject['payment-type'] === 'payment-plan';

  if (hasTilaMetafield && isPaymentPlan) {
    variantsWithTilaMetafields = {
      ...variant,
      metafields: metafieldsObject,
      name: name
    };
  }

  return variantsWithTilaMetafields;
};
